import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { Dialog, DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { EMPTY, fromEvent, Observable, switchMap } from 'rxjs';
import { map, throttleTime } from 'rxjs/operators';
import {
  AddTreatmentItem,
  AvatarsComponent,
  CheckboxComponent,
  Person,
  RequiredInputArchConfiguration,
  RequiredInputData,
  RequiredInputQuadrantConfiguration,
  RequiredInputRangeConfiguration,
  RequiredInputSurfaceConfiguration,
  RequiredInputToothConfiguration,
  RequiredInputType,
  TooltipModule,
  Tooth,
} from '@libs/ui-lib';
import { SiteTreatment } from '@ffd/api-client';
import {
  AddNewTreatmentData,
  determineTeethSelectorMode,
  isSurfaceSelectorResult,
  mapRequiredInputDialogResult,
  TeethSelectorDialogComponent,
  TeethSelectorDialogData,
  TeethSelectorResult,
} from '@libs/charting';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  SurfaceSelectorDialogComponent,
  SurfaceSelectorDialogData,
  SurfaceSelectorResult,
} from '../../../../../charting/src/lib/components/surface-selector-dialog';
import { TreatmentItemPillComponent } from './components/treatment-item-pill/treatment-item-pill.component';

export interface CompleteAppointmentModalComponentData {
  treatments: AddNewTreatmentData[];
  teeth: Tooth[];
  selectedPatient?: Person;
  appointmentDate: string;
  reopen?: boolean;
}

export interface CompleteAppointmentModalComponentResult {
  completedTreatments: AddTreatmentItem[];
  skip?: boolean;
}

@Component({
  selector: 'lib-complete-appointment-modal',
  imports: [CommonModule, TranslatePipe, AvatarsComponent, CheckboxComponent, TooltipModule, TreatmentItemPillComponent],
  templateUrl: './complete-appointment-modal.component.html',
  styleUrl: './complete-appointment-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompleteAppointmentModalComponent {
  private readonly dialog = inject(Dialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialogRef = inject(DialogRef);

  protected scrollContainer = viewChild<ElementRef<HTMLDivElement>>('scrollContainer');
  protected bottomGradient = viewChild<ElementRef<HTMLDivElement>>('bottomGradient');

  protected dialogData: CompleteAppointmentModalComponentData = inject(DIALOG_DATA);
  protected readonly treatments = signal<AddNewTreatmentData[]>(this.dialogData.treatments);
  protected selectedTreatments = signal<AddTreatmentItem[]>(this.treatments() as AddTreatmentItem[]);
  protected attemptedSubmit = signal(false);

  protected isAllSelected = computed(() => {
    return this.selectedTreatments().length === this.treatments().length;
  });

  protected hasValidationError = computed(() => {
    const selectedTreatments = this.selectedTreatments();
    const treatmentsList = this.treatments();
    const treatmentsWithMissingInputs = treatmentsList.filter((treatment) => {
      const isSelected = selectedTreatments.some((selected) => selected.dentalCode === treatment.dentalCode);
      return (
        isSelected && treatment.requiredInputType && treatment.requiredInputType !== RequiredInputType.Mouth && !treatment.requiredInputData
      );
    });
    return treatmentsWithMissingInputs.length > 0;
  });

  constructor() {
    afterNextRender(() => {
      const container = this.scrollContainer()?.nativeElement;
      const gradient = this.bottomGradient()?.nativeElement;

      if (container && gradient) {
        const THROTTLE_MS = 16;
        fromEvent(container, 'scroll')
          .pipe(
            throttleTime(THROTTLE_MS, undefined, { leading: true, trailing: true }),
            map(() => {
              const scrollTop = container.scrollTop;
              const scrollHeight = container.scrollHeight;
              const clientHeight = container.clientHeight;
              const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
              const fadeThreshold = 80;
              return Math.min(1, distanceFromBottom / fadeThreshold);
            }),
          )
          .subscribe((opacity) => {
            gradient.style.opacity = opacity.toString();
          });
      }
    });
  }

  protected isItemSelected = computed(() => {
    const selected = this.selectedTreatments();
    return (item: SiteTreatment) => selected.some((selectedTreatment) => selectedTreatment.dentalCode === item.dentalCode);
  });

  public closeDialog(): void {
    this.dialogRef.close();
  }

  public onItemChecked(item: AddTreatmentItem): void {
    this.selectedTreatments.update((current) => {
      return current.some((selectedTreatment) => selectedTreatment.dentalCode === item.dentalCode)
        ? current.filter((selectedTreatment) => selectedTreatment.dentalCode !== item.dentalCode)
        : [...current, item];
    });
  }

  public toggleSelectAll(): void {
    if (this.isAllSelected()) {
      this.selectedTreatments.set([]);
    } else {
      this.selectedTreatments.set(this.treatments());
    }
  }

  public openRequiredInputModal(treatment: AddNewTreatmentData, isSurface?: boolean, index?: number): void {
    const hasData = !!treatment.requiredInputData;

    if (isSurface && hasData) {
      this.selectSurface$(treatment, [(treatment.requiredInputData as RequiredInputSurfaceConfiguration)?.position], true)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((result) => {
          if (result) {
            this.handleSelectorResult(result, treatment, index);
          }
        });
    } else {
      this.openToothSelectorDialog(treatment, hasData, index);
    }
  }

  protected completeAppointment(skipTreatments?: boolean): void {
    if (skipTreatments) {
      this.dialogRef.close({
        completedTreatments: [],
        skip: true,
      });
    } else {
      this.attemptedSubmit.set(true);
      // Check if all selected treatments have their required inputs set
      const selectedTreatments = this.selectedTreatments();
      const treatmentsList = this.treatments();
      const treatmentsWithMissingInputs = treatmentsList.filter((treatment) => {
        // Check if this treatment is selected
        const isSelected = selectedTreatments.some((selected) => selected.dentalCode === treatment.dentalCode);
        // If selected and has a required input type but no data, it's missing inputs
        return (
          isSelected &&
          treatment.requiredInputType &&
          treatment.requiredInputType !== RequiredInputType.Mouth &&
          !treatment.requiredInputData
        );
      });

      if (treatmentsWithMissingInputs.length > 0) {
        console.warn('Cannot complete appointment: The following treatments are missing required inputs:', treatmentsWithMissingInputs);
        return; // Don't close the dialog
      }
      this.dialogRef.close({
        completedTreatments: this.selectedTreatments(),
      } as CompleteAppointmentModalComponentResult);
    }
  }

  private openToothSelectorDialog(treatment: AddNewTreatmentData, hasData: boolean, index?: number): void {
    const teethSelectorModal = this.dialog.open<TeethSelectorResult, TeethSelectorDialogData, TeethSelectorDialogComponent>(
      TeethSelectorDialogComponent,
      {
        backdropClass: ['backdrop-blur-overlay', 'bg-overlay-light'],
        disableClose: true,
        data: {
          teeth: this.dialogData.teeth,
          treatment,
          editing: hasData,
          mode: determineTeethSelectorMode(treatment.requiredInputType, hasData),
          selectedTeeth: (treatment.requiredInputData as RequiredInputToothConfiguration)?.position
            ? [(treatment.requiredInputData as RequiredInputToothConfiguration)?.position]
            : (treatment.requiredInputData as RequiredInputRangeConfiguration)?.positions,
          selectedArches: (treatment.requiredInputData as RequiredInputArchConfiguration)?.arches,
          selectedQuadrants: (treatment.requiredInputData as RequiredInputQuadrantConfiguration)?.quadrants,
        },
      },
    );

    teethSelectorModal.closed
      .pipe(
        switchMap((result) => {
          return result && treatment.requiredInputType === RequiredInputType.Surface
            ? this.selectSurface$(treatment, result.selectedTeeth, false).pipe(
                switchMap((surfaceResult) => {
                  if (surfaceResult && surfaceResult.modifySelectionConfig) {
                    teethSelectorModal.close();
                    this.openToothSelectorDialog(
                      {
                        ...treatment,
                        requiredInputData: {
                          positions: result.selectedTeeth,
                        } as RequiredInputRangeConfiguration,
                      },
                      hasData,
                      index,
                    );
                    return EMPTY;
                  }
                  return [surfaceResult];
                }),
              )
            : [result];
        }),
      )
      .subscribe((result) => {
        if (result) {
          this.handleSelectorResult(result, treatment, index);
        }
      });
  }

  private selectSurface$(
    treatment: AddNewTreatmentData,
    selectedPositions: number[],
    isEditMode: boolean,
  ): Observable<SurfaceSelectorResult | undefined> {
    const selectedTeeth = this.dialogData.teeth.filter((tooth) => selectedPositions.includes(tooth.position));

    return this.dialog.open<SurfaceSelectorResult, SurfaceSelectorDialogData, SurfaceSelectorDialogComponent>(
      SurfaceSelectorDialogComponent,
      {
        backdropClass: ['backdrop-blur-overlay', 'bg-overlay-light'],
        disableClose: true,
        data: {
          treatment,
          selectedTeeth,
          isEditMode,
        },
      },
    ).closed;
  }

  private handleSelectorResult(result: SurfaceSelectorResult | TeethSelectorResult, treatment: AddNewTreatmentData, index?: number): void {
    const newTreatments = isSurfaceSelectorResult(result)
      ? result.selectedTeethWithSurface.map((tooth) => ({
          ...treatment,
          requiredInputData: { position: tooth.position, surfaces: tooth.surfaces } as RequiredInputData,
        }))
      : mapRequiredInputDialogResult(treatment, result);

    this.treatments.update((currentTreatments) => {
      if (index === undefined) {
        return currentTreatments;
      }

      const result = [...currentTreatments];
      result.splice(index, 1, ...newTreatments);
      return result;
    });

    // Update selectedTreatments to reference the new treatment objects
    this.selectedTreatments.update((currentSelected) => {
      const wasSelected = currentSelected.some((selected) => selected.dentalCode === treatment.dentalCode);
      if (!wasSelected) {
        return currentSelected;
      }

      // Remove old treatment and add all new treatments (in case of surface selector creating multiple)
      return [...currentSelected.filter((selected) => selected.dentalCode !== treatment.dentalCode), ...newTreatments];
    });
  }
}
